import { motion } from 'framer-motion';
import styled from 'styled-components';

import Background from 'assets/images/landing/background.png';
import { setTypography, setVh } from 'utils/styles/mixins';
import {
  mediaDesktop,
  mediaMobile,
  mediaTablet,
} from 'utils/styles/responsive';
import { colors } from 'utils/styles/theme';

export const Container = styled(motion.div)`
  width: 100vw;
  min-height: ${setVh(100)};
  height: 100%;
  background: ${colors.pearlBush};
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: url(${Background});
    background-size: cover;
    background-repeat: no-repeat;
    user-select: none;
    z-index: 0;
  }
`;

export const LogoWrapper = styled(motion.div)`
  position: absolute;
  top: 64rem;
  left: 0;
  user-select: none;
  z-index: 2;

  svg {
    width: 450rem;
  }

  ${mediaDesktop`
    top: 132rem;
    svg {
      width: 500rem;
    }
  `}
`;

export const Wrapper = styled(motion.div)`
  display: grid;
  height: 100%;
  grid-template-columns: 1fr;
  gap: 80rem;
  position: relative;
  z-index: 1;
  padding: 224rem 0;
  overflow-x: hidden;
  overflow-y: auto;

  ${mediaTablet`
    grid-template-columns: 20vw 1fr min-content;
    padding: 0;
  `}
`;

export const ImageWrapper = styled(motion.div)<{ isWindowTooSmall?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80rem 0;
  max-width: 100vw;

  img {
    max-width: 700rem;
  }

  ${mediaTablet`
    padding: 0;
    img {
      transform: translateX(-15%);
      max-width: 900rem;
    }
  `}

  ${mediaDesktop`
    padding: 80rem 0;
    display: flex;
    img {
      transform: translateX(-15%);
      max-width: 1765rem;
    }
  `}
  ${mediaMobile`
  padding: 10rem 0;

  img {
    max-width: 550rem;
  }
`}
`;

export const ContentWrapper = styled(motion.div)<{
  isWindowTooSmall?: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0;

  ${({ isWindowTooSmall }) =>
    isWindowTooSmall &&
    `
    width: max-content;
    margin: auto;
  `};

  ${mediaTablet`
    align-items: flex-start;
  `}

  ${mediaMobile`
  justify-content: flex-start;
`}
`;

export const Title = styled(motion.div)<{ isWindowTooSmall?: boolean }>`
  color: ${colors.spicyMix};
  ${({ theme: { locale } }) => setTypography('heading1', locale)};
  white-space: pre-wrap;
  margin: 0 0 100rem;
  text-align: ${({ isWindowTooSmall }) =>
    isWindowTooSmall ? 'left' : 'center'};

  h1 {
    user-select: none;
  }

  ${mediaTablet`
    font-size: 220rem;
    text-align: left;
  `}

  ${mediaDesktop`
    font-size: 240rem;
  `}
  ${mediaMobile`
  margin: 0 0 70rem;
`}
`;

export const Description = styled(motion.div)<{
  isWindowTooSmall?: boolean;
  isUnsupportedBrowserError?: boolean;
}>`
  color: ${colors.taupe};
  ${({ theme: { locale } }) => setTypography('body3', locale)};
  white-space: pre-wrap;
  user-select: none;
  margin: ${({ isUnsupportedBrowserError }) =>
    isUnsupportedBrowserError ? '60rem 0 20rem' : '10rem 0 20rem'};
  position: relative;
  text-align: center;
  padding: 0 30rem;
  width: ${({ isWindowTooSmall }) => (isWindowTooSmall ? '100%' : 'auto')};

  p {
    user-select: none;
  }

  span {
    font-weight: bold;

    span {
      color: ${colors.spicyMix};
    }
  }

  ${mediaTablet`
    text-align: left;
    padding: 0;
  `}
`;

export const ButtonWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 128rem 48rem 68rem;
  margin-top: 100rem;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0.6) 50%,
      rgba(255, 255, 255, 0.1) 100%
    );
    z-index: 1;
    filter: blur(2px);
  }

  button {
    width: 505rem;
    height: 158rem;
    z-index: 2;
  }

  ${mediaTablet`
    position: relative;
    padding: 0;
    justify-content: flex-start;
    align-items: unset;

    &:before {
      display: none;
    }

    button {
      width: 375rem;
      height: 115rem;
      z-index: 2;
    }

    div {
      width: 375rem;
      height: 115rem;
    }
  `}

  ${mediaDesktop`
    button {
      width: 505rem;
      height: 158rem;
      z-index: 2;
    }
  `}
`;

export const Browsers = styled(motion.div)`
  width: 100%;
  justify-content: space-around;
  display: flex;
  margin: 80rem 0 0;
  align-items: flex-start;

  ${mediaTablet`
    width: auto;
    justify-content: center;
  `}
`;

export const Browser = styled(motion.div)`
  width: 80rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }

  p {
    margin-top: 40rem;
    text-align: center;
    color: ${colors.taupe};
    ${({ theme: { locale } }) => setTypography('body3', locale)};
  }

  ${mediaTablet`
    width: 150rem;
    margin-right: 110rem;
    align-items: center;
    justify-content: center;
  `}
`;
